import { FC } from 'react';
import { Button } from '@mui/material';
import { observer } from 'mobx-react';
import { AxiosError } from 'axios';
import BaseConfig from './BaseConfig';
import useCompanyId from '../../../hooks/useCompanyId';
import { usePaymentMethodForm } from '../../paymethod/fields/usePaymentMethodForm';
import {
  DefaultNamesForPaymentMethodConfigTypes,
  DefaultShortNamesForPaymentMethodConfigTypes,
  PaymentMethodConfigTypes,
} from '../../../model/PaymentMethodConfig';
import { TidypayPaymentMethod } from '../../../model/PaymentMethod';
import { useCreatePaymentMethodMutation } from '../../../stores/FetcherStores/PaymentMethodFetcher';
import SharedPaymentMethodFieldGroup from '../../paymethod/fields/SharedPaymentMethodFieldGroup';
import { usePrices, useResellerPrices } from '../../../stores/FetcherStores/PricesFetcher';
import { useGetCompany } from '../../../stores/FetcherStores/CompanyFetcher';

interface TidypayConfigProps {
  onClose: () => void;
}

const TidypayConfig: FC<TidypayConfigProps> = observer(({ onClose }) => {
  const companyId = useCompanyId();
  const { data: company } = useGetCompany(companyId);
  const { data: prices } = usePrices();
  const { data: resellerPrices } = useResellerPrices(company?.resellerId || '');
  const {
    mutateAsync: createPaymentMethod,
    isLoading: isCreating,
    error: createError,
  } = useCreatePaymentMethodMutation();

  const form = usePaymentMethodForm({
    defaultValues: {
      baseFields: {
        name: DefaultNamesForPaymentMethodConfigTypes[PaymentMethodConfigTypes.TIDYPAY],
        shortName: DefaultShortNamesForPaymentMethodConfigTypes[PaymentMethodConfigTypes.TIDYPAY],
        active: true,
        bookkeepingAccount: '',
      },
    },
    onSubmit: async ({ value }) => {
      const newPaymentMethod: Omit<
        TidypayPaymentMethod,
        'id' | 'accountId' | 'onboardingStatus' | 'onboardingUrl'
      > = {
        type: PaymentMethodConfigTypes.TIDYPAY,
        name: value.baseFields.name,
        shortName: value.baseFields.shortName,
        active: value.baseFields.active,
        bookkeepingAccount: value.baseFields.bookkeepingAccount,
      };
      await createPaymentMethod(companyId, newPaymentMethod)
        .then(() => {
          onClose();
        })
        .catch(() => {});
    },
  });

  return (
    <BaseConfig
      title="Kortbetalning - Tidypay"
      description="Ta kortbetalningar via Tidypay"
      price={`${prices?.recurring.TIDYPAY_TERMINAL.price.format()}/mån`}
      resellerPrice={
        resellerPrices ? `${resellerPrices?.recurring.TIDYPAY_TERMINAL.price.format()}/mån` : ''
      }
      onClose={onClose}
      onSubmit={() => {
        form.handleSubmit();
      }}
      errorMessage={
        createError instanceof AxiosError
          ? createError?.response?.data?.name || createError?.message
          : createError?.message
      }
      buttons={
        <>
          <Button variant="outlined" onClick={onClose} color="secondary">
            Avbryt
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isCreating}
            sx={{
              backgroundColor: '#1db954',
              '&:hover': {
                backgroundColor: '#1a9f4a',
              },
            }}
          >
            {isCreating ? 'Lägger till...' : 'Lägg till'}
          </Button>
        </>
      }
    >
      <form.AppForm>
        <SharedPaymentMethodFieldGroup form={form} fields="baseFields" />
      </form.AppForm>
    </BaseConfig>
  );
});

export default TidypayConfig;
